import { useState, useEffect, createContext, useContext } from 'react';

export type Theme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  toggleTheme: () => {},
});

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

/* ─── All light-mode overrides injected via JS ─────────────────────────
   This approach bypasses PostCSS selector-escaping issues with `/` chars
   (e.g. text-white/80) by injecting a <style> tag directly into <head>.
   Dynamically-added stylesheets always come AFTER static ones in the
   cascade, so these rules win without needing !important fights.
────────────────────────────────────────────────────────────────────── */
const LIGHT_OVERRIDES = `
  html.light body {
    background-color: #f0f4fc;
    color: #0a1628;
  }

  /* ── Section backgrounds ── */
  html.light .bg-navy-950 { background-color: #f0f4fc !important; }
  html.light .bg-navy-900 { background-color: #f8fbff !important; }
  html.light .bg-navy-800 { background-color: #ffffff !important; }
  html.light .bg-navy-700 { background-color: #dde6f5 !important; }

  html.light .bg-navy-900\\/60  { background-color: rgba(248,251,255,0.95) !important; }
  html.light .bg-navy-900\\/95  { background-color: rgba(255,255,255,0.96) !important; }
  html.light .bg-navy-900\\/98  { background-color: rgba(255,255,255,0.99) !important; }
  html.light .bg-navy-800\\/60  { background-color: rgba(255,255,255,0.90) !important; }
  html.light .bg-navy-800\\/40  { background-color: rgba(255,255,255,0.80) !important; }
  html.light .bg-white\\/10     { background-color: rgba(10,22,40,0.06) !important; }
  html.light .bg-white\\/20     { background-color: rgba(10,22,40,0.10) !important; }
  html.light .hover\\:bg-white\\/20:hover { background-color: rgba(10,22,40,0.14) !important; }

  /* ── Borders ── */
  html.light .border-navy-700         { border-color: #c5d4e8 !important; }
  html.light .border-navy-600         { border-color: #c5d4e8 !important; }
  html.light .border-navy-500         { border-color: #a3bacd !important; }
  html.light .hover\\:border-navy-500:hover { border-color: #8aabcb !important; }
  html.light .border-white\\/20       { border-color: rgba(10,22,40,0.20) !important; }

  /* ── Text: solid white → dark navy ── */
  html.light .text-white      { color: #0a1628 !important; }

  /* ── Text: white with opacity variants → dark navy equivalent ── */
  html.light .text-white\\/90  { color: rgba(10,22,40,0.92) !important; }
  html.light .text-white\\/80  { color: rgba(10,22,40,0.82) !important; }
  html.light .text-white\\/75  { color: rgba(10,22,40,0.76) !important; }
  html.light .text-white\\/70  { color: rgba(10,22,40,0.70) !important; }
  html.light .text-white\\/65  { color: rgba(10,22,40,0.65) !important; }
  html.light .text-white\\/60  { color: rgba(10,22,40,0.60) !important; }
  html.light .text-white\\/55  { color: rgba(10,22,40,0.55) !important; }
  html.light .text-white\\/50  { color: rgba(10,22,40,0.50) !important; }
  html.light .text-white\\/45  { color: rgba(10,22,40,0.45) !important; }
  html.light .text-white\\/40  { color: rgba(10,22,40,0.40) !important; }
  html.light .text-white\\/35  { color: rgba(10,22,40,0.35) !important; }
  html.light .text-white\\/30  { color: rgba(10,22,40,0.30) !important; }
  html.light .text-white\\/25  { color: rgba(10,22,40,0.25) !important; }
  html.light .text-white\\/20  { color: rgba(10,22,40,0.20) !important; }
  html.light .text-white\\/15  { color: rgba(10,22,40,0.15) !important; }
  html.light .text-white\\/10  { color: rgba(10,22,40,0.10) !important; }

  /* ── Placeholders ── */
  html.light .placeholder-white\\/25::placeholder { color: rgba(10,22,40,0.28) !important; }

  /* ── Form inputs ── */
  html.light input    { background-color: #ffffff !important; color: #0a1628 !important; border-color: #c5d4e8 !important; }
  html.light textarea { background-color: #ffffff !important; color: #0a1628 !important; border-color: #c5d4e8 !important; }
  html.light select   { background-color: #ffffff !important; color: #0a1628 !important; border-color: #c5d4e8 !important; }
  html.light input::placeholder    { color: rgba(10,22,40,0.28) !important; }
  html.light textarea::placeholder { color: rgba(10,22,40,0.28) !important; }
  html.light select option         { background-color: #ffffff; color: #0a1628; }

  /* ── Navbar scrolled state ── */
  html.light .bg-navy-950\\/80 { background-color: rgba(240,244,252,0.90) !important; }

  /* ════════════════════════════════════════════════════════════════════
     EXCEPTION: Sections with permanent dark overlays (Hero, FinalCTA).
     These always have a dark bg-image overlay so text must stay WHITE
     regardless of the active theme.
  ════════════════════════════════════════════════════════════════════ */
  html.light [data-always-dark] .text-white      { color: #ffffff !important; }
  html.light [data-always-dark] .text-white\\/90  { color: rgba(255,255,255,0.90) !important; }
  html.light [data-always-dark] .text-white\\/80  { color: rgba(255,255,255,0.80) !important; }
  html.light [data-always-dark] .text-white\\/75  { color: rgba(255,255,255,0.75) !important; }
  html.light [data-always-dark] .text-white\\/70  { color: rgba(255,255,255,0.70) !important; }
  html.light [data-always-dark] .text-white\\/65  { color: rgba(255,255,255,0.65) !important; }
  html.light [data-always-dark] .text-white\\/60  { color: rgba(255,255,255,0.60) !important; }
  html.light [data-always-dark] .text-white\\/55  { color: rgba(255,255,255,0.55) !important; }
  html.light [data-always-dark] .text-white\\/50  { color: rgba(255,255,255,0.50) !important; }
  html.light [data-always-dark] .text-white\\/45  { color: rgba(255,255,255,0.45) !important; }
  html.light [data-always-dark] .text-white\\/40  { color: rgba(255,255,255,0.40) !important; }
  html.light [data-always-dark] .text-white\\/35  { color: rgba(255,255,255,0.35) !important; }
  html.light [data-always-dark] .text-white\\/30  { color: rgba(255,255,255,0.30) !important; }
  html.light [data-always-dark] .text-white\\/25  { color: rgba(255,255,255,0.25) !important; }
  html.light [data-always-dark] .text-white\\/20  { color: rgba(255,255,255,0.20) !important; }
  html.light [data-always-dark] .text-white\\/15  { color: rgba(255,255,255,0.15) !important; }
  html.light [data-always-dark] .text-white\\/10  { color: rgba(255,255,255,0.10) !important; }
  html.light [data-always-dark] .hover\\:text-white\\/70:hover { color: rgba(255,255,255,0.70) !important; }
  html.light [data-always-dark] .hover\\:text-white\\/80:hover { color: rgba(255,255,255,0.80) !important; }
  html.light [data-always-dark] .border-white\\/20 { border-color: rgba(255,255,255,0.20) !important; }
  html.light [data-always-dark] .border-white\\/30 { border-color: rgba(255,255,255,0.30) !important; }
  html.light [data-always-dark] .border-white\\/40 { border-color: rgba(255,255,255,0.40) !important; }
  html.light [data-always-dark] .bg-white\\/10     { background-color: rgba(255,255,255,0.10) !important; }
  html.light [data-always-dark] .bg-white\\/20     { background-color: rgba(255,255,255,0.20) !important; }
  html.light [data-always-dark] .hover\\:bg-white\\/20:hover { background-color: rgba(255,255,255,0.20) !important; }

  /* ════════════════════════════════════════════════════════════════════
     EXCEPTION: Readdy / Vapi widget — always keep original white colors.
     The widget has its own dark skin; light-mode overrides must not touch it.
  ════════════════════════════════════════════════════════════════════ */
  html.light [id^="vapi-"] .text-white,
  html.light [id^="vapi-"] .text-white\\/90,
  html.light [id^="vapi-"] .text-white\\/80,
  html.light [id^="vapi-"] .text-white\\/70,
  html.light [id^="vapi-"] .text-white\\/60,
  html.light [id^="vapi-"] .text-white\\/50,
  html.light [class*="vapi"] .text-white,
  html.light [class*="vapi"] .text-white\\/90,
  html.light [class*="vapi"] .text-white\\/80,
  html.light [class*="vapi"] .text-white\\/70,
  html.light [class*="vapi"] .text-white\\/60,
  html.light [class*="vapi"] .text-white\\/50,
  html.light [id^="readdy-"] .text-white,
  html.light [id^="readdy-"] .text-white\\/90,
  html.light [id^="readdy-"] .text-white\\/80,
  html.light [id^="readdy-"] .text-white\\/70,
  html.light [id^="readdy-"] .text-white\\/60,
  html.light [id^="readdy-"] .text-white\\/50,
  html.light [class*="readdy"] .text-white,
  html.light [class*="readdy"] .text-white\\/90,
  html.light [class*="readdy"] .text-white\\/80,
  html.light [class*="readdy"] .text-white\\/70,
  html.light [class*="readdy"] .text-white\\/60,
  html.light [class*="readdy"] .text-white\\/50 {
    color: #ffffff !important;
  }
  html.light [id^="vapi-"],
  html.light [class*="vapi"],
  html.light [id^="readdy-"],
  html.light [class*="readdy"] {
    color-scheme: dark;
  }

  /* ════════════════════════════════════════════════════════════════════
     CHATBOT FLOAT — ID-level specificity (1,0,0,0), UNBEATABLE.
     Guarantees the floating bot button & tooltip always stay dark
     regardless of what other rules the light theme injects.
  ════════════════════════════════════════════════════════════════════ */
  html.light #innoflex-chatbot-float {
    color-scheme: dark !important;
  }
  html.light #innoflex-chatbot-float button,
  html.light #innoflex-chatbot-float > div {
    background-color: #060f23 !important;
  }
  html.light #innoflex-chatbot-float,
  html.light #innoflex-chatbot-float * {
    color: #ffffff !important;
  }
  html.light #innoflex-chatbot-float .aw-cyan,
  html.light #innoflex-chatbot-float .aw-cyan-85 {
    color: #00cfff !important;
  }
  html.light #innoflex-chatbot-float .aw-90  { color: rgba(255,255,255,0.90) !important; }
  html.light #innoflex-chatbot-float .aw-70  { color: rgba(255,255,255,0.70) !important; }
  html.light #innoflex-chatbot-float .aw-65  { color: rgba(255,255,255,0.65) !important; }
  html.light #innoflex-chatbot-float .aw-60  { color: rgba(255,255,255,0.60) !important; }
  html.light #innoflex-chatbot-float .aw-50  { color: rgba(255,255,255,0.50) !important; }
  html.light #innoflex-chatbot-float .aw-45  { color: rgba(255,255,255,0.45) !important; }
  html.light #innoflex-chatbot-float .aw-40  { color: rgba(255,255,255,0.40) !important; }
  html.light #innoflex-chatbot-float .aw-35  { color: rgba(255,255,255,0.35) !important; }
  html.light #innoflex-chatbot-float .aw-20  { color: rgba(255,255,255,0.20) !important; }
  html.light #innoflex-chatbot-float input,
  html.light #innoflex-chatbot-float textarea {
    background-color: rgba(6,15,35,0.80) !important;
    color: #ffffff !important;
    border-color: rgba(0,207,255,0.30) !important;
  }
`;

function injectLightStyles(): void {
  let el = document.getElementById('innoflex-light-theme') as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = 'innoflex-light-theme';
    document.head.appendChild(el);
  }
  el.textContent = LIGHT_OVERRIDES;
}

function removeLightStyles(): void {
  const el = document.getElementById('innoflex-light-theme');
  if (el) el.remove();
}

export function useThemeInit(): ThemeContextValue {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('innoflex-theme');
    if (saved === 'light' || saved === 'dark') return saved as Theme;
    return 'dark';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.classList.add('light');
      root.classList.remove('dark');
      injectLightStyles();
    } else {
      root.classList.remove('light');
      root.classList.add('dark');
      removeLightStyles();
    }
    localStorage.setItem('innoflex-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    const root = document.documentElement;
    root.classList.add('theme-transitioning');
    setTimeout(() => root.classList.remove('theme-transitioning'), 400);
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  return { theme, toggleTheme };
}
